import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Album, Eye, EyeOff, Download, Trash2, EyeOff as HideIcon, Loader2, Check, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientLoginPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [albumCode, setAlbumCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!albumCode.trim() || !password.trim()) {
      setError(t('fillAllFields') || "Por favor completa todos los campos");
      return;
    }

    setIsLoading(true);
    try {
      // First search for the album
      const searchResponse = await axios.get(`${API}/api/albums/search?q=${encodeURIComponent(albumCode.trim())}`);
      
      if (!searchResponse.data.found || !searchResponse.data.album) {
        setError(t('albumNotFound') || "Álbum no encontrado");
        setIsLoading(false);
        return;
      }
      
      const album = searchResponse.data.album;
      
      // Check if album is private (requires password)
      if (!album.is_private) {
        setError(t('albumNotPrivate') || "Este álbum es público. Usa la búsqueda de invitados.");
        setIsLoading(false);
        return;
      }
      
      // Verify password
      const verifyResponse = await axios.post(`${API}/api/albums/${album.share_code}/verify-password`, {
        password: password.trim()
      });
      
      if (verifyResponse.data.valid) {
        // Store client access in sessionStorage
        sessionStorage.setItem(`client_access_${album.share_code}`, 'true');
        toast.success(t('welcomeToAlbum') || `¡Bienvenido a ${album.name}!`);
        navigate(`/album/${album.share_code}?client=true`);
      } else {
        setError(t('incorrectPassword') || "Contraseña incorrecta");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError(t('incorrectPassword') || "Contraseña incorrecta");
      } else if (err.response?.status === 404) {
        setError(t('albumNotFound') || "Álbum no encontrado");
      } else {
        setError(t('errorOccurred') || "Ocurrió un error. Intenta de nuevo.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: <Download className="w-6 h-6 text-indigo-600" />,
      title: t('downloadPhotos') || "Descarga todas las fotos",
      description: t('downloadPhotosDesc') || "Descarga las fotos individuales o todas en un ZIP con un solo clic"
    },
    {
      icon: <HideIcon className="w-6 h-6 text-amber-600" />,
      title: t('hidePhotos') || "Oculta fotos",
      description: t('hidePhotosDesc') || "Oculta fotos de la vista de los invitados sin eliminarlas"
    },
    {
      icon: <Trash2 className="w-6 h-6 text-red-500" />,
      title: t('deletePhotos') || "Elimina fotos",
      description: t('deletePhotosDesc') || "Elimina permanentemente las fotos que no desees"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-pink-50 flex flex-col">
      {/* Header */}
      <header className="w-full py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2"
          >
            <img src="/mf-logo.png" alt="MFcreativemanager" className="w-10 h-10 object-contain" />
            <span 
              className="font-bold text-xl text-slate-900 hidden sm:block"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {t('appName')}
            </span>
          </Link>
          <LanguageSwitcher variant="minimal" />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* Left Side - Login Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-slate-100">
              {/* Back button */}
              <Link 
                to="/"
                className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 text-sm mb-6"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('backToHome') || 'Volver al inicio'}
              </Link>
              
              {/* Title */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-pink-600 flex items-center justify-center mx-auto mb-4">
                  <Album className="w-8 h-8 text-white" />
                </div>
                <h1 
                  className="text-2xl font-bold text-slate-900"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  {t('clientAccess') || 'Acceso a tu álbum'}
                </h1>
                <p className="text-slate-500 mt-2">
                  {t('clientAccessDesc') || 'Ingresa con el código y contraseña que te proporcionaron'}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Album Code/Name */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-2">
                    {t('albumCodeOrName') || 'Código o nombre del álbum'}
                  </label>
                  <div className="relative">
                    <Album className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      value={albumCode}
                      onChange={(e) => setAlbumCode(e.target.value)}
                      placeholder={t('enterAlbumCode') || 'Ej: Mi Boda 2025 o abc123'}
                      className="h-12 pl-11 rounded-xl"
                      data-testid="client-album-code"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-2">
                    {t('albumPassword') || 'Contraseña del álbum'}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('enterAlbumPassword') || 'Contraseña proporcionada'}
                      className="h-12 pl-11 pr-11 rounded-xl"
                      data-testid="client-password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-pink-600 text-white font-semibold"
                  data-testid="client-login-submit"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('verifying') || 'Verificando...'}
                    </div>
                  ) : (
                    t('accessMyAlbum') || 'Acceder a mi álbum'
                  )}
                </Button>
              </form>
            </div>

            {/* Right Side - Features */}
            <div className="lg:pl-8">
              <h2 
                className="text-xl md:text-2xl font-bold text-slate-900 mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {t('albumBenefits') || '¿Qué puedes hacer con tu álbum?'}
              </h2>
              
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {feature.title}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Additional info */}
              <div className="mt-8 p-4 bg-indigo-50 rounded-2xl border border-indigo-100">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-indigo-900 font-medium">
                      {t('clientTip') || 'Tu contraseña fue proporcionada por MFcreativemanager'}
                    </p>
                    <p className="text-xs text-indigo-700 mt-1">
                      {t('clientTipDesc') || 'Si no la tienes, contacta por Instagram @mfcreativemanager'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
